import { useMemo } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { useNotify } from '@/shared/toast/ToastProvider'
import { useAccountDeletionStatus, useCancelAccountDeletion } from '../api/useAccountDeletion'
import { DELETION_COPY } from '../lib/deletion-disclosure'

function formatScheduledPurgeAt(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

export function PendingDeletionBanner() {
  const statusQuery = useAccountDeletionStatus()
  const cancelMutation = useCancelAccountDeletion()
  const { showToast } = useNotify()

  const scheduledLabel = useMemo(
    () => formatScheduledPurgeAt(statusQuery.data?.scheduledPurgeAt),
    [statusQuery.data?.scheduledPurgeAt]
  )

  if (statusQuery.isLoading || !statusQuery.data?.pending) {
    return null
  }

  const daysRemaining =
    typeof statusQuery.data.daysRemaining === 'number'
      ? statusQuery.data.daysRemaining
      : null

  const handleCancel = () => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => {
        showToast(DELETION_COPY.cancelDeletionSuccess, 'success')
      },
      onError: () => {
        showToast(DELETION_COPY.cancelDeletionError, 'error')
      },
    })
  }

  return (
    <Box
      sx={{ px: { xs: 2, md: 3 }, pt: 2, maxWidth: 1200, mx: 'auto', width: '100%' }}
      data-testid="pending-deletion-banner"
    >
      <Alert
        severity="warning"
        sx={{
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
          '& .MuiAlert-action': {
            ml: { xs: 0, sm: 'auto' },
            pt: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          },
        }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            data-testid="pending-deletion-cancel"
            sx={{ whiteSpace: 'nowrap', mt: 0.25 }}
          >
            {cancelMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              DELETION_COPY.cancelDeletionCta
            )}
          </Button>
        }
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {DELETION_COPY.pendingBannerTitle}
        </Typography>
        <Typography variant="body2">
          {DELETION_COPY.pendingBannerBody(daysRemaining, scheduledLabel)}
        </Typography>
      </Alert>
    </Box>
  )
}
